/**
 * Données factices du mode `?demo` (vérification visuelle hors API, S2-g).
 * En mémoire, mutées par les mêmes fonctions que l'API réelle simule.
 */
import {
  DEFINITIONS_MODULES,
  dependancesManquantes,
  estModule,
  estModuleCoeur,
  estModuleOptionnel,
  estThemeRegie,
  modulesDeLaGalerie,
  modulesDependantDe,
  MODULES_DEMONSTRATION,
  normaliserModulesActifs,
  REGISTRE_MODULES,
} from '@artosera/shared';
import { ApiError } from './api.js';
import {
  basculerModulePalierDemo,
  basculerPalierDemo,
  modulesParPalierDemo,
  paliersDemo,
} from './demoPaliers.js';
import { vueDAigleDemo } from './demoVueDAigle.js';
import type {
  EtatSsoConsole,
  FiltresTravaux,
  PageTravaux,
  AccesSupport,
  RoleAccesSupport,
  ApparenceGalerie,
  CoupureModule,
  Domaine,
  ExportGalerie,
  RenvoiExport,
  GalerieFiche,
  GalerieListe,
  IdPalierDev,
  Interrupteurs,
  JournalEntree,
  Me,
  NomVueDAigle,
  NouvelleGalerie,
  PaliersVue,
  PeriodeVueDAigle,
  ReponseModules,
  RoleConsole,
  ReponseConnecteursConsole,
  ConnecteurConsole,
  Sante,
  Travail,
  VueDAigle,
} from '../types.js';

function id(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * Une fiche en mémoire, sans sa liste EFFECTIVE de modules : celle-ci se
 * recalcule à chaque lecture avec `modulesDeLaGalerie`, comme l'API — la même
 * fonction, les mêmes coupures globales.
 */
/**
 * `estTest` (lot B) est facultatif ici : les galeries de démonstration
 * antérieures ne le portent pas, et ne sont pas des espaces de test.
 */
type FicheStockee = Omit<GalerieFiche, 'modules' | 'estTest'> & { estTest?: boolean };

const fiches = new Map<string, FicheStockee>();

/**
 * Interrupteurs globaux de démonstration : le coffre est coupé pour tout le
 * parc, pour montrer cet état dans l'onglet Modules. La variable de secours
 * reste vide.
 */
const coupes: CoupureModule[] = [
  {
    module: 'coffre',
    motif: 'Rappels d’échéance en échec, le temps du correctif.',
    auteurEmail: 'demo@artosera.com',
    coupeLe: '2026-09-22T06:30:00.000Z',
  },
];
const coupesSecours: string[] = [];

function ensembleCoupes(): Set<string> {
  return new Set([...coupes.map((coupure) => coupure.module), ...coupesSecours]);
}

function avecModules(fiche: FicheStockee): GalerieFiche {
  return {
    ...fiche,
    estTest: fiche.estTest === true,
    modules: [...modulesDeLaGalerie({ modulesActifs: fiche.modulesActifs }, ensembleCoupes())],
  };
}

function semer(): void {
  const base: Array<Omit<FicheStockee, 'id'>> = [
    {
      slug: 'sainte-avoye',
      nom: 'Galerie Sainte-Avoye',
      palier: 'galerie',
      etat: 'active',
      // Modules figés de démonstration, plus le site qu'elle a acheté (comme au
      // semis de l'API) : toute extension exige une décision écrite.
      modulesActifs: [...MODULES_DEMONSTRATION, 'site'],
      creeLe: '2026-02-11T09:00:00.000Z',
      dernierePublication: '2026-09-18T16:04:00.000Z',
      derniereSauvegarde: '2026-09-22T02:10:00.000Z',
      groupe: null,
      quotas: { stockageGo: 50, stockageUtiliseGo: 12.4, membresMax: 10 },
      stockage: { octets: 13314398617, objets: 4210, mesureLe: '2026-09-22T04:00:00.000Z' },
      domaines: [
        {
          hote: 'sainte-avoye.artosera.gallery',
          etat: 'actif',
          cible: 'sainte-avoye.artosera.gallery',
          verifieLe: '2026-02-11T09:05:00.000Z',
          principal: true,
          parDefaut: true,
          dns: null,
        },
        {
          hote: 'www.sainte-avoye.example',
          etat: 'a_verifier',
          cible: 'sainte-avoye.artosera.gallery',
          verifieLe: null,
          principal: false,
          parDefaut: false,
          dns: { type: 'CNAME', valeur: 'sainte-avoye.artosera.gallery' },
        },
      ],
      membres: [
        {
          email: 'claire.vallier@sainte-avoye.example',
          role: 'direction',
          dernierAcces: '2026-09-21T08:12:00.000Z',
          invitationEnAttente: false,
          compteId: id(),
          droits: {
            voirPrixArtiste: false,
            coffreConfidentiel: false,
            emettreFactures: false,
            publierSite: false,
          },
        },
        {
          email: 'equipe@sainte-avoye.example',
          role: 'equipe',
          dernierAcces: '2026-09-19T14:30:00.000Z',
          invitationEnAttente: false,
          compteId: id(),
          droits: {
            voirPrixArtiste: false,
            coffreConfidentiel: false,
            emettreFactures: false,
            publierSite: false,
          },
        },
      ],
      sauvegardes: [
        { date: '2026-09-22T02:10:00.000Z', taille: 812345678, etat: 'reussie' },
        { date: '2026-09-21T02:10:00.000Z', taille: 809876543, etat: 'reussie' },
      ],
      exports: [
        {
          id: 'export-demo-1',
          jobId: 'travail-demo-1',
          origine: 'mensuel',
          final: false,
          etat: 'pret',
          demandeLe: '2026-09-01T04:00:00.000Z',
          pretLe: '2026-09-01T04:04:00.000Z',
          expireLe: '2026-09-08T04:04:00.000Z',
          octets: 812345678,
          sha256: '9f2c1d0a7b3e4f5a6b7c8d9e0f1a2b3c4d5e6f708192a3b4c5d6e7f8091a2b3c',
          fichiers: 1284,
          envoyeLe: '2026-09-01T04:04:10.000Z',
          telechargeLe: null,
          accuseLe: null,
          erreur: null,
        },
      ],
      supportAccess: null,
      purge: null,
      theme: 'halo',
      choixThemeClient: false,
    },
    {
      slug: 'galerie-du-marais',
      nom: 'Galerie du Marais',
      palier: 'groupe',
      etat: 'active',
      // Facturation, stand et site, activés un à un depuis la console (D21).
      modulesActifs: ['facturation', 'mode-foire', 'site'],
      creeLe: '2026-04-03T09:00:00.000Z',
      dernierePublication: '2026-09-20T11:00:00.000Z',
      derniereSauvegarde: '2026-09-22T02:12:00.000Z',
      groupe: 'Groupe Marais Art',
      quotas: { stockageGo: 200, stockageUtiliseGo: 88.1, membresMax: 30 },
      stockage: { octets: 94590140416, objets: 28140, mesureLe: '2026-09-22T04:00:00.000Z' },
      domaines: [
        {
          hote: 'galerie-du-marais.artosera.gallery',
          etat: 'actif',
          cible: 'galerie-du-marais.artosera.gallery',
          verifieLe: '2026-04-03T09:05:00.000Z',
          principal: true,
          parDefaut: true,
          dns: null,
        },
      ],
      membres: [
        {
          email: 'direction@galerie-du-marais.example',
          role: 'direction',
          dernierAcces: '2026-09-22T07:50:00.000Z',
          invitationEnAttente: false,
          compteId: id(),
          droits: {
            voirPrixArtiste: false,
            coffreConfidentiel: false,
            emettreFactures: false,
            publierSite: false,
          },
        },
      ],
      sauvegardes: [{ date: '2026-09-22T02:12:00.000Z', taille: 1223456789, etat: 'reussie' }],
      exports: [],
      supportAccess: { actif: true, expireLe: '2026-09-22T20:00:00.000Z' },
      purge: null,
      // Lot C : un thème choisi par la console, que la galerie peut changer.
      theme: 'structure',
      choixThemeClient: true,
    },
    {
      slug: 'atelier-nord',
      nom: 'Atelier Nord',
      palier: 'essentiel',
      etat: 'suspendue',
      // Le cœur seul : aucun module complémentaire.
      modulesActifs: [],
      creeLe: '2025-11-20T09:00:00.000Z',
      dernierePublication: null,
      derniereSauvegarde: '2026-09-15T02:10:00.000Z',
      groupe: null,
      quotas: { stockageGo: 20, stockageUtiliseGo: 3.2, membresMax: 5 },
      stockage: { octets: 3435973837, objets: 1120, mesureLe: '2026-09-22T04:00:00.000Z' },
      domaines: [
        {
          hote: 'atelier-nord.artosera.gallery',
          etat: 'desactive',
          cible: 'atelier-nord.artosera.gallery',
          verifieLe: '2025-11-20T09:05:00.000Z',
          principal: true,
          parDefaut: true,
          dns: null,
        },
      ],
      membres: [
        {
          email: 'contact@atelier-nord.example',
          role: 'direction',
          dernierAcces: '2026-08-30T09:00:00.000Z',
          invitationEnAttente: false,
          compteId: id(),
          droits: {
            voirPrixArtiste: false,
            coffreConfidentiel: false,
            emettreFactures: false,
            publierSite: false,
          },
        },
      ],
      sauvegardes: [{ date: '2026-09-15T02:10:00.000Z', taille: 401234567, etat: 'echouee' }],
      exports: [],
      supportAccess: null,
      purge: null,
      theme: 'halo',
      choixThemeClient: false,
    },
  ];
  for (const fiche of base) {
    const nouvelId = id();
    fiches.set(nouvelId, { ...fiche, id: nouvelId });
  }
}
semer();

const travaux: Travail[] = [
  {
    id: id(),
    type: 'provisionnement',
    etat: 'reussi',
    tentatives: 1,
    runAt: '2026-09-22T09:00:00.000Z',
    lastError: null,
  },
  {
    id: id(),
    type: 'publication_site',
    etat: 'echoue',
    tentatives: 3,
    runAt: '2026-09-22T08:40:00.000Z',
    lastError: 'Délai dépassé en publiant le site.',
  },
  {
    id: id(),
    type: 'sauvegarde_nocturne',
    etat: 'en_cours',
    tentatives: 1,
    runAt: '2026-09-22T02:00:00.000Z',
    lastError: null,
  },
  {
    id: id(),
    type: 'export_complet',
    etat: 'en_attente',
    tentatives: 0,
    runAt: '2026-09-22T10:00:00.000Z',
    lastError: null,
  },
  {
    id: id(),
    type: 'brevo:sync',
    // Le module newsletter est coupé : le travail est écarté, pas en échec.
    etat: 'ignore_module_inactif',
    tentatives: 1,
    runAt: '2026-09-22T07:00:00.000Z',
    lastError: null,
  },
  {
    id: id(),
    type: 'backup:restore-test',
    etat: 'abandonne',
    tentatives: 5,
    runAt: '2026-09-20T02:00:00.000Z',
    lastError: 'Nombre maximal de tentatives atteint.',
  },
];

const journal: JournalEntree[] = [
  {
    date: '2026-09-22T07:50:00.000Z',
    acteur: 'venio.direction@artosera.com',
    action: 'ACCES_SUPPORT_ACCORDE',
    cible: 'galerie-du-marais',
    details: 'motif=diagnostic de publication',
    metadonnees: { motif: 'diagnostic de publication' },
  },
  {
    date: '2026-09-21T18:20:00.000Z',
    acteur: 'claire.vallier@sainte-avoye.example',
    // Le refus n'est pas un geste de la console — c'est la direction de la
    // galerie qui répond — mais il reste visible ici (docs/dev/api.md § 12.5,
    // engagement 04). C'est la seule trace qu'en garde la console.
    action: 'ACCES_SUPPORT_REFUSE',
    cible: 'sainte-avoye',
    details: null,
  },
  {
    date: '2026-09-21T16:04:00.000Z',
    acteur: 'venio.exploitation@artosera.com',
    action: 'CONSOLE_MODULE_ACTIVE',
    cible: 'sainte-avoye',
    details: 'module=mode-foire',
    metadonnees: { module: 'mode-foire', roleConsole: 'venio_exploitation' },
  },
  {
    date: '2026-09-21T09:12:00.000Z',
    acteur: 'venio.exploitation@artosera.com',
    action: 'CONSOLE_GALERIE_MODIFIEE',
    cible: 'sainte-avoye',
    details: 'avant={"palier":"essentiel"} · apres={"palier":"galerie"}',
    metadonnees: {
      avant: { palier: 'essentiel', etat: 'active', quotas: { membresMax: 5 } },
      apres: { palier: 'galerie', quotas: { membresMax: 10 } },
      roleConsole: 'venio_exploitation',
    },
  },
  {
    date: '2026-09-21T08:40:00.000Z',
    acteur: 'venio.direction@artosera.com',
    action: 'CONNEXION_REUSSIE',
    cible: '',
    details: 'galeries=0',
    metadonnees: { galeries: 0 },
  },
  {
    date: '2026-09-20T11:00:00.000Z',
    acteur: 'venio.exploitation@artosera.com',
    action: 'CONSOLE_DOMAINE_VERIFIE',
    cible: 'galerie-du-marais.artosera.gallery',
    details: null,
  },
];

const sante: Sante = {
  status: 'ok',
  version: '0.0.0-demo',
  environment: 'demo',
  uptime: 348213,
  plateforme: { ok: true, etat: 1, pingMs: 4, base: 'artosera_platform' },
  galeries: {
    connexionsOuvertes: 3,
    inactiviteMaximaleMs: 612000,
    bases: ['sainte-avoye', 'galerie-du-marais', 'atelier-nord'],
  },
  derniereMigration: { id: '0007-index-domaines', appliqueeLe: '2026-09-10T03:00:00.000Z' },
  verifieLe: new Date().toISOString(),
};

export const compteDemo: Me = {
  compte: {
    id: 'demo-compte',
    email: 'demo@artosera.com',
    nom: 'Compte de démonstration',
    langue: 'fr',
    rolePlateforme: 'venio_direction',
    deuxiemeFacteurActive: true,
    graceMfaJusquau: null,
  },
  galeries: [],
};

const CLE_DEMO_ROLE = 'artosera-console-demo-role';

/** `?demo&role=…` : les quatre comptes de démonstration, un par rôle de la console. */
const ROLES_DEMO: Record<string, RoleConsole> = {
  associe: 'venio_associe',
  support: 'venio_support',
  exploitation: 'venio_exploitation',
  direction: 'venio_direction',
};

/**
 * Le rôle du compte de démonstration : `role` dans l'URL, ou celui d'une
 * visite précédente de l'onglet (les liens internes ne portent pas la
 * requête). Comme `?demo` (`modeDemo`), le rôle est MÉMORISÉ pour l'onglet
 * (`sessionStorage`) : pour en changer, rouvrir l'application avec un autre
 * `?demo&role=…`. La direction par défaut.
 */
export function roleDemo(): RoleConsole {
  if (typeof window === 'undefined') return 'venio_direction';
  const demande = new URLSearchParams(window.location.search).get('role');
  if (demande !== null && demande in ROLES_DEMO) {
    try {
      window.sessionStorage.setItem(CLE_DEMO_ROLE, demande);
    } catch {
      /* stockage indisponible : le rôle vaut pour cette page seulement */
    }
    return ROLES_DEMO[demande]!;
  }
  try {
    const memorise = window.sessionStorage.getItem(CLE_DEMO_ROLE);
    if (memorise !== null && memorise in ROLES_DEMO) return ROLES_DEMO[memorise]!;
  } catch {
    /* stockage indisponible */
  }
  return 'venio_direction';
}

/** Le compte de démonstration du rôle courant (`roleDemo`). */
export function compteDemoCourant(): Me {
  const role = roleDemo();
  if (role === 'venio_direction') return compteDemo;
  const identite: Record<
    Exclude<RoleConsole, 'venio_direction'>,
    { nom: string; email: string }
  > = {
    venio_associe: { nom: 'Solène Varenne', email: 'solene.varenne@associes.example' },
    venio_support: { nom: 'Compte support de démonstration', email: 'support@demo.example' },
    venio_exploitation: {
      nom: 'Compte exploitation de démonstration',
      email: 'exploitation@demo.example',
    },
  };
  return {
    ...compteDemo,
    compte: { ...compteDemo.compte, ...identite[role], id: `demo-${role}`, rolePlateforme: role },
  };
}

function versListe(stockee: FicheStockee): GalerieListe {
  const fiche = avecModules(stockee);
  return {
    id: fiche.id,
    slug: fiche.slug,
    nom: fiche.nom,
    palier: fiche.palier,
    etat: fiche.etat,
    estTest: fiche.estTest,
    modulesActifs: fiche.modulesActifs,
    modules: fiche.modules,
    creeLe: fiche.creeLe,
    domaines: fiche.domaines.length,
    membres: fiche.membres.length,
    dernierePublication: fiche.dernierePublication,
    derniereSauvegarde: fiche.derniereSauvegarde,
    stockage: fiche.stockage,
  };
}

/** Le registre vu depuis une galerie : même calcul que `vueModules` côté API. */
function reponseModules(fiche: FicheStockee): ReponseModules {
  const coupe = ensembleCoupes();
  const effectifs = modulesDeLaGalerie({ modulesActifs: fiche.modulesActifs }, coupe);
  return {
    id: fiche.id,
    palier: fiche.palier,
    modulesActifs: fiche.modulesActifs,
    modules: DEFINITIONS_MODULES.filter((definition) => definition.famille !== 'addon').map(
      (definition) => ({
        id: definition.id,
        famille: definition.famille,
        disponible: definition.disponible,
        dependances: [...definition.dependances],
        dependants: [...modulesDependantDe(definition.id)],
        active: estModuleCoeur(definition.id) || fiche.modulesActifs.includes(definition.id),
        effectif: effectifs.includes(definition.id),
        coupeGlobalement: coupe.has(definition.id),
        dependancesManquantes: [...dependancesManquantes(definition.id, fiche.modulesActifs)],
      }),
    ),
  };
}

function ficheModifiable(idGalerie: string): FicheStockee {
  const fiche = fiches.get(idGalerie);
  if (!fiche) throw new ApiError(404, 'ressource.introuvable', 'Galerie introuvable.');
  if (fiche.etat === 'resiliee') {
    throw new ApiError(409, 'conflit.version', 'Une galerie résiliée ne se modifie plus.', {
      raison: 'galerie_resiliee',
    });
  }
  return fiche;
}

/** Le module réglable d'une route, ou l'erreur que rendrait l'API. */
function moduleReglable(module: string): void {
  if (!estModule(module)) throw new ApiError(404, 'ressource.introuvable', 'Module inconnu.');
  if (!estModuleOptionnel(module)) {
    throw new ApiError(
      400,
      'validation.invalide',
      'Ce module fait partie du cœur : il est toujours actif et ne se désactive pas.',
      { raison: 'module_coeur', module },
    );
  }
}

function vueInterrupteurs(): Interrupteurs {
  return {
    coupes: coupes.map((coupure) => ({ ...coupure })),
    coupesSecours: [...coupesSecours],
    modules: modulesParPalierDemo(),
  };
}

/** L'urgence vue des paliers : indépendante de leur déblocage. */
function urgenceDemo(): { coupes: ReadonlySet<string>; secours: ReadonlySet<string> } {
  return {
    coupes: new Set(coupes.map((coupure) => coupure.module)),
    secours: new Set(coupesSecours),
  };
}

export const demoApi = {
  obtenirSso: (_id: string): Promise<EtatSsoConsole> =>
    Promise.resolve({
      moduleActif: false,
      identitesLiees: 0,
      sessionsOuvertes: 0,
      derniereConnexionLe: null,
    }),
  obtenirConnecteurs: (_id: string): Promise<ReponseConnecteursConsole> => {
    const vide = (id: ConnecteurConsole['id'], module: string): ConnecteurConsole => ({
      id,
      module,
      mode: 'simule',
      configure: false,
      finSecret: null,
      parametres: {},
      verifieLe: null,
      erreur: null,
      revision: 0,
      moduleActif: false,
    });
    return Promise.resolve({
      connecteurs: [
        vide('brevo', 'newsletter-reseaux'),
        vide('artsy', 'artsy-artnet'),
        vide('artnet', 'artsy-artnet'),
        vide('stripe', 'boutique'),
        {
          ...vide('oidc', 'sso'),
          configure: true,
          revision: 1,
          parametres: {
            emetteur: 'https://id.sainte-avoye.example',
            clientId: 'artosera-demo',
            domainesAutorises: 'sainte-avoye.demo',
          },
        },
      ],
      simulation: true,
    });
  },

  listerGaleries(): GalerieListe[] {
    return [...fiches.values()].map(versListe);
  },
  obtenirGalerie(idGalerie: string): GalerieFiche | undefined {
    const fiche = fiches.get(idGalerie);
    return fiche ? avecModules(fiche) : undefined;
  },
  obtenirModules(idGalerie: string): ReponseModules {
    const fiche = fiches.get(idGalerie);
    if (!fiche) throw new ApiError(404, 'ressource.introuvable', 'Galerie introuvable.');
    return reponseModules(fiche);
  },
  basculerModule(idGalerie: string, module: string, actif: boolean): ReponseModules {
    const fiche = ficheModifiable(idGalerie);
    moduleReglable(module);
    if (actif && estModule(module)) {
      if (!REGISTRE_MODULES[module].disponible) {
        throw new ApiError(
          403,
          'module.indisponible',
          'Ce module n’est pas encore livré : il ne peut pas être activé.',
          { raison: 'module_non_disponible', module },
        );
      }
      const manquantes = dependancesManquantes(module, fiche.modulesActifs);
      if (manquantes.length > 0) {
        throw new ApiError(409, 'conflit.version', 'Activez d’abord les modules requis.', {
          raison: 'dependances_manquantes',
          module,
          dependances: manquantes,
        });
      }
    }
    // Désactiver ne touche à aucune donnée : seule la liste change.
    const suivante = actif
      ? normaliserModulesActifs([...fiche.modulesActifs, module])
      : normaliserModulesActifs(fiche.modulesActifs.filter((valeur) => valeur !== module));
    const nouvelle = { ...fiche, modulesActifs: suivante };
    fiches.set(idGalerie, nouvelle);
    return reponseModules(nouvelle);
  },
  remplacerModules(idGalerie: string, modulesActifs: readonly string[]): ReponseModules {
    const fiche = ficheModifiable(idGalerie);
    const nouvelle = { ...fiche, modulesActifs: normaliserModulesActifs(modulesActifs) };
    fiches.set(idGalerie, nouvelle);
    return reponseModules(nouvelle);
  },
  obtenirInterrupteurs(): Interrupteurs {
    return vueInterrupteurs();
  },
  basculerInterrupteur(module: string, coupe: boolean, motif: string): Interrupteurs {
    moduleReglable(module);
    if (motif.trim().length < 3) {
      throw new ApiError(400, 'validation.invalide', 'Indiquez le motif.');
    }
    const rang = coupes.findIndex((coupure) => coupure.module === module);
    if (coupe && rang === -1) {
      coupes.push({
        module,
        motif: motif.trim(),
        auteurEmail: compteDemo.compte.email,
        coupeLe: new Date().toISOString(),
      });
    } else if (!coupe && rang !== -1) {
      coupes.splice(rang, 1);
    }
    return vueInterrupteurs();
  },
  obtenirPaliers(): PaliersVue {
    return paliersDemo(urgenceDemo());
  },
  basculerPalier(
    palier: IdPalierDev,
    debloque: boolean,
    motif: string,
    revision: number,
  ): PaliersVue {
    return basculerPalierDemo(palier, debloque, motif, revision, urgenceDemo());
  },
  basculerModulePalier(
    module: string,
    debloque: boolean,
    motif: string,
    revision: number,
  ): PaliersVue {
    return basculerModulePalierDemo(module, debloque, motif, revision, urgenceDemo());
  },
  creerGalerie(payload: NouvelleGalerie): { id: string; slug: string; invitationEnvoyee: boolean } {
    const nouvelId = id();
    fiches.set(nouvelId, {
      id: nouvelId,
      slug: payload.slug,
      nom: payload.nom,
      palier: payload.palier,
      etat: 'active',
      estTest: payload.estTest,
      // Une galerie neuve n'a que le cœur ; l'exploitant active ensuite ses
      // modules un à un depuis l'onglet Modules (D21).
      modulesActifs: [],
      creeLe: new Date().toISOString(),
      dernierePublication: null,
      derniereSauvegarde: null,
      groupe: null,
      quotas: { stockageGo: 20, stockageUtiliseGo: 0, membresMax: 5 },
      // Galerie tout juste provisionnee : `storage:measure` n'est pas encore passe.
      stockage: null,
      domaines: [
        {
          hote: `${payload.slug}.artosera.gallery`,
          etat: 'actif',
          cible: `${payload.slug}.artosera.gallery`,
          verifieLe: new Date().toISOString(),
          principal: true,
          parDefaut: true,
          dns: null,
        },
      ],
      membres: [
        {
          email: payload.direction.email,
          role: 'direction',
          dernierAcces: null,
          // La direction d'une galerie neuve n'a pas encore accepté (lot B).
          invitationEnAttente: true,
          compteId: id(),
          droits: {
            voirPrixArtiste: false,
            coffreConfidentiel: false,
            emettreFactures: false,
            publierSite: false,
          },
        },
      ],
      sauvegardes: [],
      exports: [],
      supportAccess: null,
      purge: null,
      theme: 'halo',
      choixThemeClient: false,
    });
    return { id: nouvelId, slug: payload.slug, invitationEnvoyee: true };
  },
  renvoyerInvitation(
    idGalerie: string,
    compteId: string,
  ): {
    email: string;
    expireLe: string;
    invitationEnvoyee: boolean;
  } {
    const direction = fiches
      .get(idGalerie)
      ?.membres.find(
        (membre) =>
          membre.compteId === compteId && membre.role === 'direction' && membre.invitationEnAttente,
      );
    if (!direction) {
      throw new ApiError(
        409,
        'conflit.version',
        'La direction de cette galerie a déjà accepté son invitation.',
        { raison: 'direction_deja_active' },
      );
    }
    return {
      email: direction.email,
      expireLe: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      invitationEnvoyee: true,
    };
  },
  marquerEspaceTest(idGalerie: string, estTest: boolean): { id: string; estTest: boolean } {
    const fiche = fiches.get(idGalerie);
    if (fiche) fiches.set(idGalerie, { ...fiche, estTest });
    return { id: idGalerie, estTest };
  },
  modifierApparence(
    idGalerie: string,
    payload: Partial<Pick<GalerieFiche, 'theme' | 'choixThemeClient'>>,
  ): ApparenceGalerie {
    const fiche = ficheModifiable(idGalerie);
    if (payload.theme !== undefined && !estThemeRegie(payload.theme)) {
      throw new ApiError(400, 'validation.invalide', 'Thème inconnu.');
    }
    const nouvelle = {
      ...fiche,
      theme: payload.theme ?? fiche.theme,
      choixThemeClient: payload.choixThemeClient ?? fiche.choixThemeClient,
    };
    fiches.set(idGalerie, nouvelle);
    return { id: idGalerie, theme: nouvelle.theme, choixThemeClient: nouvelle.choixThemeClient };
  },
  patchGalerie(idGalerie: string, payload: Partial<Pick<GalerieFiche, 'palier' | 'etat'>>): void {
    const fiche = fiches.get(idGalerie);
    if (!fiche) return;
    fiches.set(idGalerie, { ...fiche, ...payload });
  },
  ajouterDomaine(idGalerie: string, hote: string): Domaine {
    const fiche = fiches.get(idGalerie);
    const cible = `${fiche?.slug ?? 'galerie'}.artosera.gallery`;
    // Démonstration : deux étiquettes valent un domaine nu (l'API, elle, lit la
    // liste des suffixes publics). Adresse de documentation (RFC 5737).
    const nu = hote.split('.').length === 2;
    const domaine: Domaine = {
      hote,
      etat: 'a_verifier',
      cible,
      verifieLe: null,
      principal: false,
      parDefaut: false,
      dns: nu
        ? { type: 'A', valeurs: ['203.0.113.10'], valeursAaaa: [] }
        : { type: 'CNAME', valeur: cible },
    };
    if (fiche) fiches.set(idGalerie, { ...fiche, domaines: [...fiche.domaines, domaine] });
    return domaine;
  },
  verifierDomaine(idGalerie: string, hote: string): void {
    const fiche = fiches.get(idGalerie);
    if (!fiche) return;
    fiches.set(idGalerie, {
      ...fiche,
      domaines: fiche.domaines.map((d) =>
        d.hote === hote ? { ...d, etat: 'actif', verifieLe: new Date().toISOString() } : d,
      ),
    });
  },
  rendreDomainePrincipal(idGalerie: string, hote: string): Domaine {
    const fiche = fiches.get(idGalerie);
    const domaines = (fiche?.domaines ?? []).map((d) => ({ ...d, principal: d.hote === hote }));
    if (fiche) fiches.set(idGalerie, { ...fiche, domaines });
    const domaine = domaines.find((d) => d.hote === hote);
    if (!domaine) throw new Error(`Domaine inconnu : ${hote}`);
    return domaine;
  },
  retirerDomaine(idGalerie: string, hote: string): void {
    const fiche = fiches.get(idGalerie);
    if (!fiche) return;
    fiches.set(idGalerie, { ...fiche, domaines: fiche.domaines.filter((d) => d.hote !== hote) });
  },
  demanderExport(idGalerie: string): { jobId: string } {
    const fiche = fiches.get(idGalerie);
    if (fiche) {
      const nouveau: ExportGalerie = {
        id: null,
        jobId: id(),
        origine: 'console',
        final: false,
        etat: 'en_attente',
        demandeLe: new Date().toISOString(),
        pretLe: null,
        expireLe: null,
        octets: null,
        sha256: null,
        fichiers: null,
        envoyeLe: null,
        telechargeLe: null,
        accuseLe: null,
        erreur: null,
      };
      fiches.set(idGalerie, { ...fiche, exports: [nouveau, ...fiche.exports] });
    }
    return { jobId: id() };
  },
  renvoyerExport(_idGalerie: string, _idExport: string): RenvoiExport {
    return {
      destinataires: 1,
      transmis: 1,
      expireLe: new Date(Date.now() + 7 * 24 * 3_600_000).toISOString(),
    };
  },
  demanderTestRestauration(_idGalerie: string): { jobId: string } {
    return { jobId: id() };
  },
  demanderAccesSupport(idGalerie: string, _motif: string, _role: RoleAccesSupport): AccesSupport {
    const acces: AccesSupport = {
      actif: false,
      expireLe: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
    };
    const fiche = fiches.get(idGalerie);
    if (fiche) fiches.set(idGalerie, { ...fiche, supportAccess: acces });
    return acces;
  },
  revoquerAccesSupport(idGalerie: string): void {
    const fiche = fiches.get(idGalerie);
    if (fiche) fiches.set(idGalerie, { ...fiche, supportAccess: null });
  },
  resilierGalerie(idGalerie: string): void {
    const fiche = fiches.get(idGalerie);
    if (!fiche) return;
    const resilieeLe = new Date();
    fiches.set(idGalerie, {
      ...fiche,
      etat: 'resiliee',
      purge: {
        resilieeLe: resilieeLe.toISOString(),
        possibleLe: new Date(resilieeLe.getTime() + 90 * 24 * 3_600_000).toISOString(),
        accuseLe: null,
        demandeeLe: null,
        demandeePar: null,
        debuteLe: null,
        purgeeLe: null,
        effacementCompletPrevuLe: null,
        effacementCompletLe: null,
        forcee: false,
        motif: null,
        operationsEnCours: 0,
        repriseSuperviseeRequise: null,
        historiqueDepotsInconnu: false,
        versionHistoriqueDepots: 0,
      },
    });
  },
  reprendrePurgeSupervisee(idGalerie: string): { jobId: string } {
    const fiche = fiches.get(idGalerie);
    if (fiche?.purge) {
      fiches.set(idGalerie, {
        ...fiche,
        purge: { ...fiche.purge, repriseSuperviseeRequise: null },
      });
    }
    return { jobId: id() };
  },
  verifierHistoriqueDepots(
    idGalerie: string,
    depots: readonly string[],
  ): { historiqueDepotsInconnu: false; depotsSauvegarde: string[] } {
    const fiche = fiches.get(idGalerie);
    if (fiche?.purge) {
      fiches.set(idGalerie, {
        ...fiche,
        purge: { ...fiche.purge, historiqueDepotsInconnu: false },
      });
    }
    return { historiqueDepotsInconnu: false, depotsSauvegarde: [...depots] };
  },
  remettreOperationsAZero(idGalerie: string): { operationsEnCours: 0; avant: number } {
    const fiche = fiches.get(idGalerie);
    const avant = fiche?.purge?.operationsEnCours ?? 0;
    if (fiche?.purge) {
      fiches.set(idGalerie, { ...fiche, purge: { ...fiche.purge, operationsEnCours: 0 } });
    }
    return { operationsEnCours: 0, avant };
  },
  lancerPurge(idGalerie: string, motif?: string): { jobId: string } {
    const fiche = fiches.get(idGalerie);
    if (fiche?.purge) {
      fiches.set(idGalerie, {
        ...fiche,
        purge: {
          ...fiche.purge,
          demandeeLe: new Date().toISOString(),
          demandeePar: 'demo@artosera.com',
          forcee: fiche.purge.forcee || motif !== undefined,
          motif: motif ?? fiche.purge.motif,
        },
      });
    }
    return { jobId: id() };
  },
  listerTravaux(): Travail[] {
    return travaux;
  },
  /** La même page que l'API : filtres, 50 travaux par page, types connus. */
  pageTravaux(filtres: FiltresTravaux & { page: number }): PageTravaux {
    const retenus = travaux.filter(
      (travail) =>
        (filtres.etat === undefined || travail.etat === filtres.etat) &&
        (filtres.etat !== undefined ||
          filtres.masquerIgnores !== true ||
          travail.etat !== 'ignore_module_inactif') &&
        (filtres.type === undefined || travail.type === filtres.type),
    );
    const taille = 50;
    return {
      items: retenus.slice((filtres.page - 1) * taille, filtres.page * taille),
      page: filtres.page,
      total: retenus.length,
      types: [...new Set(travaux.map((travail) => travail.type))].sort(),
    };
  },
  relancerTravail(idTravail: string): void {
    const travail = travaux.find((t) => t.id === idTravail);
    if (travail) {
      travail.etat = 'en_attente';
      travail.tentatives += 1;
      travail.lastError = null;
    }
  },
  listerJournal(): JournalEntree[] {
    return journal;
  },
  sante(): Sante {
    return sante;
  },
  vueDAigle(periode: PeriodeVueDAigle, vue?: NomVueDAigle): VueDAigle {
    return vueDAigleDemo(roleDemo(), periode, vue);
  },
};
